import moment from 'moment'
import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Button, Modal, Page, Spinner } from '../../components'
import { appsStyleSelected } from '../../helpers/app'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { images } from '../../images'
import { AuthenticatedRoutable, IActivePass, IPassGroup, ITemporalPass } from '../../models'
import { urls } from '../../routing'
import { LiteralsService } from '../../services'
import { buyPassAction, fetchActivePassesAction, fetchAvailablePassesAction } from '../../store/actions'
import { BuyPassPhase, IStoreState } from '../../store/states'
import StyledSection, { StyledConfirmation } from './styles'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  activePasses: IActivePass[]
  isFetchingActivePasses: boolean
  availablePasses: IPassGroup[]
  isFetchingAvailablePasses: boolean
  buyPassPhase: BuyPassPhase
  stripeIds: any[]
  paymentMethodLastFour: string
}

interface IState {
  showNewPass: boolean
  selectedPass?: ITemporalPass
  selectedPassAccountId?: string
}

class Wallet extends AuthenticatedRoutable<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = { showNewPass: false }
  }

  public componentDidMount(): void {
    super.componentDidMount()
    this.props.dispatch(fetchActivePassesAction())
  }

  public componentDidUpdate(prevProps: IProps): void {
    const { buyPassPhase } = this.props
    const wasBuying = prevProps.buyPassPhase === BuyPassPhase.CREATING || prevProps.buyPassPhase === BuyPassPhase.CONFIRMING
    const hasSettled = buyPassPhase === BuyPassPhase.SUCCESS ||
      buyPassPhase === BuyPassPhase.POLLING ||
      buyPassPhase === BuyPassPhase.FAILED

    // Payment resolved one way or the other: back to the wallet, where the new pass will land.
    if (wasBuying && hasSettled) {
      this.setState({ showNewPass: false, selectedPass: undefined, selectedPassAccountId: undefined })
    }
  }

  public render(): ReactNode {
    const { showNewPass } = this.state

    return (
      <Page className={ `authenticated ${ appsStyleSelected?.style }` }>
        <StyledSection>
          { showNewPass ? this.availablePassesContent : this.walletContent }
        </StyledSection>
        { this.confirmationModal }
      </Page>
    )
  }

  private get hasCard(): boolean {
    const { stripeIds, paymentMethodLastFour } = this.props

    return !!stripeIds?.length && !!paymentMethodLastFour
  }

  private get isBuying(): boolean {
    const { buyPassPhase } = this.props

    return buyPassPhase === BuyPassPhase.CREATING || buyPassPhase === BuyPassPhase.CONFIRMING
  }

  private get walletContent(): ReactNode {
    const { isFetchingActivePasses, buyPassPhase } = this.props
    // While polling the pass is paid for but not listed yet, so keep the spinner up.
    const isLoading = isFetchingActivePasses || buyPassPhase === BuyPassPhase.POLLING

    return (
      <>
        { this.paymentMethodSection }
        <section>
          <h2>{ LiteralsService.get('activePasses', true) }</h2>
          { isLoading ? this.spinner : this.activePasses }
        </section>
        {
          !isLoading &&
          <div className="actions">
            <Button
              title={ LiteralsService.get('buyPass') }
              icon="plusWhite"
              onPress={ this.openNewPass }
              filled
              upper
            />
          </div>
        }
      </>
    )
  }

  private get paymentMethodSection(): ReactNode {
    const { paymentMethodLastFour } = this.props

    return (
      <section className="payment-method">
        <h2>{ LiteralsService.get('paymentMethod', true) }</h2>
        <div className="card" onClick={ this.goToPaymentMethod }>
          <img src={ images.paymentMethod } alt="card" />
          {
            this.hasCard
              ? <span className="number">{ `•••• •••• •••• ${ paymentMethodLastFour }` }</span>
              : <span className="add">{ LiteralsService.get('addPaymentMethod', true) }</span>
          }
          <img className="arrow" src={ images.arrowRight } alt="" />
        </div>
      </section>
    )
  }

  private get activePasses(): ReactNode {
    const { activePasses } = this.props

    if (!activePasses.length) {
      return (
        <div className="empty">
          <span>{ LiteralsService.get('noActivePasses', true) }</span>
        </div>
      )
    }

    return (
      <div className="passes">
        { activePasses.map(pass => this.activePassCard(pass)) }
      </div>
    )
  }

  private activePassCard(pass: IActivePass): ReactNode {
    // The BE sends the expiry in UTC; the traveller reads it in their own time.
    const endDate = moment.utc(pass.endDateTime).local()

    return (
      <div className="pass-card" key={ pass.id }>
        <div className="head">
          <span className="name">{ pass.temporalPass.name }</span>
          <span className="type">{ LiteralsService.get(pass.temporalPass.type, true) }</span>
        </div>
        <div className="body">
          <div className="detail">
            <span className="label">{ LiteralsService.get('validUntil', true) }</span>
            <span className="value">{ `${ endDate.format('L') } ${ endDate.format('HH:mm') }` }</span>
          </div>
          <div className="detail">
            <span className="label">{ LiteralsService.get('usages', true) }</span>
            <span className="value">{ this.usagesLabel(pass) }</span>
          </div>
        </div>
      </div>
    )
  }

  private usagesLabel(pass: IActivePass): string {
    return pass.temporalPass.usageLimit
      ? `${ pass.usages } / ${ pass.temporalPass.usageLimit }`
      : LiteralsService.get('unlimited', true)
  }

  private get availablePassesContent(): ReactNode {
    const { availablePasses, isFetchingAvailablePasses } = this.props

    if (isFetchingAvailablePasses) return this.spinner

    // A client can be listed with no passes on sale, so the empty state follows the
    // passes themselves, not the number of groups.
    const groups = availablePasses.filter(group => group.temporalPasses?.length)

    return (
      <>
        <section>
          <h2>{ LiteralsService.get('passes', true) }</h2>
          {
            groups.length
              ? groups.map(group => (
                  <div key={ group.clientId }>
                    { groups.length > 1 && <p className="group-name">{ group.clientName }</p> }
                    <div className="passes">
                      { group.temporalPasses.map(pass => this.availablePassCard(pass, group.stripeAccountId)) }
                    </div>
                  </div>
                ))
              : <div className="empty"><span>{ LiteralsService.get('noPassesAvailable', true) }</span></div>
          }
        </section>
        <div className="actions">
          <Button title={ LiteralsService.get('cancel') } onPress={ this.closeNewPass } upper />
        </div>
      </>
    )
  }

  private availablePassCard(pass: ITemporalPass, accountId: string): ReactNode {
    return (
      <div className="pass-card available" key={ pass.id } onClick={ () => this.selectPass(pass, accountId) }>
        <div className="head">
          <span className="name">{ pass.name }</span>
          <span className="price">{ `${ pass.price.toFixed(2) } €` }</span>
        </div>
        <div className="body">
          <div className="detail">
            <span className="label">{ LiteralsService.get('type', true) }</span>
            <span className="value">{ LiteralsService.get(pass.type, true) }</span>
          </div>
          <div className="detail">
            <span className="label">{ LiteralsService.get('usages', true) }</span>
            <span className="value">{ pass.usageLimit ? pass.usageLimit : LiteralsService.get('unlimited', true) }</span>
          </div>
        </div>
      </div>
    )
  }

  private get confirmationModal(): ReactNode {
    const { selectedPass } = this.state
    if (!selectedPass) return null

    return (
      <Modal isHidden={ false } onOverlayClick={ this.dismissConfirmation }>
        <StyledConfirmation>
          <span className="title">{ LiteralsService.get('confirmPurchase', true) }</span>
          <span className="name">{ selectedPass.name }</span>
          <span className="price">{ `${ selectedPass.price.toFixed(2) } €` }</span>
          <footer>
            <Button
              title={ LiteralsService.get('confirm') }
              styles={{ width: '100%' }}
              onPress={ () => this.handleBuyPass(selectedPass.id) }
              isDisabled={ this.isBuying }
              filled
              upper
            />
            <Button
              title={ LiteralsService.get('cancel') }
              styles={{ width: '100%' }}
              onPress={ this.dismissConfirmation }
              upper
            />
          </footer>
        </StyledConfirmation>
      </Modal>
    )
  }

  private get spinner(): ReactNode {
    return (
      <article className="spinner-container">
        <Spinner />
      </article>
    )
  }

  private openNewPass = (): void => {
    this.setState({ showNewPass: true })
    this.props.dispatch(fetchAvailablePassesAction())
  }

  private closeNewPass = (): void => {
    this.setState({ showNewPass: false })
  }

  // Buying needs a card on file, so an empty wallet goes to the payment method first.
  private selectPass = (pass: ITemporalPass, accountId: string): void => {
    if (!this.hasCard) {
      this.goToPaymentMethod()
      return
    }

    this.setState({ selectedPass: pass, selectedPassAccountId: accountId })
  }

  private dismissConfirmation = (): void => {
    if (this.isBuying) return

    this.setState({ selectedPass: undefined, selectedPassAccountId: undefined })
  }

  private handleBuyPass = (passId: number): void => {
    this.props.dispatch(buyPassAction(passId, this.state.selectedPassAccountId ?? ''))
  }

  private goToPaymentMethod = (): void => {
    this.props.history.push(urls.configuration.paymentMethod)
  }
}

const mapStateToProps = ({ wallet, userInfo }: IStoreState) => ({
  activePasses: wallet.activePasses,
  isFetchingActivePasses: wallet.isFetchingActivePasses,
  availablePasses: wallet.availablePasses,
  isFetchingAvailablePasses: wallet.isFetchingAvailablePasses,
  buyPassPhase: wallet.buyPassPhase,
  stripeIds: userInfo.stripeIds,
  paymentMethodLastFour: userInfo.paymentMethodLastFour
})

export default withRouter(connectRedux(mapStateToProps, Wallet as any))
